import React, { useEffect, useState } from "react";
import api from "@/lib/api";

const actionLabels = {
  create: { color: "bg-blue-100 text-blue-800", label: "created" },
  update: { color: "bg-slate-100 text-slate-700", label: "updated" },
  delete: { color: "bg-red-100 text-red-800", label: "deleted" },
  approve: { color: "bg-emerald-100 text-emerald-800", label: "approved" },
  reject: { color: "bg-amber-100 text-amber-800", label: "rejected" },
  submit: { color: "bg-indigo-100 text-indigo-800", label: "submitted" },
  pricing_compute: { color: "bg-purple-100 text-purple-800", label: "priced" },
  ai_draft: { color: "bg-fuchsia-100 text-fuchsia-800", label: "AI drafted" },
  ai_accept: { color: "bg-emerald-100 text-emerald-800", label: "AI accepted" },
  ai_reject: { color: "bg-slate-100 text-slate-700", label: "AI rejected" },
  ai_edit: { color: "bg-slate-100 text-slate-700", label: "AI edited" },
  export_docx: { color: "bg-slate-900 text-white", label: "exported DOCX" },
  export_pdf: { color: "bg-slate-900 text-white", label: "exported PDF" },
};

const ActionPill = ({ action }) => {
  const meta = actionLabels[action] || { color: "bg-slate-100 text-slate-700", label: action };
  return <span className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-sm ${meta.color}`}>{meta.label}</span>;
};

const summariseDetail = (entry) => {
  const d = entry.detail || {};
  const parts = [];
  if (d.email) parts.push(`email=${d.email}`);
  if (d.client) parts.push(`client=${d.client}`);
  if (d.name) parts.push(`name=${d.name}`);
  if (d.key) parts.push(`key=${d.key}`);
  if (d.code) parts.push(`code=${d.code}`);
  if (d.section) parts.push(`section=${d.section}`);
  if (typeof d.line_count === "number") parts.push(`${d.line_count} line${d.line_count === 1 ? "" : "s"}`);
  if (Array.isArray(d.errors) && d.errors.length) parts.push(`${d.errors.length} pricing warning(s)`);
  if (Array.isArray(d.keys) && d.keys.length) parts.push(`fields: ${d.keys.slice(0, 3).join(", ")}${d.keys.length > 3 ? "…" : ""}`);
  if (Array.isArray(d.roles) && d.roles.length) parts.push(`roles: ${d.roles.join(", ")}`);
  return parts.join(" · ");
};

export default function AuditLogPage() {
  const [entries, setEntries] = useState([]);
  useEffect(() => { api.get("/audit").then(r => setEntries(r.data)); }, []);

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1400px]" data-testid="audit-page">
      <div className="label-micro">Traceability</div>
      <h1 className="font-display text-2xl sm:text-3xl font-black mt-1">Audit Log</h1>
      <p className="text-sm text-slate-600 mt-2 max-w-2xl">Every user action is recorded here — creating proposals, computing pricing, running AI drafts, editing libraries, exporting documents. Each row is one event.</p>

      {/* Card list — always renders cleanly on any screen, no mashed columns */}
      <div className="mt-6 space-y-2">
        {entries.length === 0 && <div className="text-sm text-slate-500 bg-white border border-slate-200 p-6 rounded-sm">No audit events yet.</div>}
        {entries.map((e) => {
          const summary = summariseDetail(e);
          return (
            <div key={e.id} className="bg-white border border-slate-200 px-4 py-3 rounded-sm" data-testid={`audit-entry-${e.id}`}>
              <div className="flex items-start gap-3 flex-wrap">
                <div className="text-xs text-slate-500 tabular-nums w-full sm:w-40 shrink-0">{new Date(e.timestamp).toLocaleString()}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <ActionPill action={e.action} />
                    <span className="text-xs text-slate-500">{e.entity}</span>
                    <span className="text-sm font-semibold text-slate-900 break-all">{e.actor_email}</span>
                  </div>
                  {summary && <div className="text-xs text-slate-600 mt-1">{summary}</div>}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
